import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../utilities/services/language.service';
import { RoleServiceService } from '../../utilities/services/role-service/role-service.service';
import { EncryptDecryptService } from '../../utilities/services/EncrptyDecrypt/encrypt-decrypt.service';
import { CommonModule } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import { GifLoaderComponent } from '../shared/gif-loader/gif-loader.component';
import { Router, RouterOutlet, ActivatedRoute } from '@angular/router';
import { SubLicenseManagementService } from './sub-license-management/sub-license-management.service';
import { NoDataFoundComponent } from '../shared/no-data-found/no-data-found.component';
import { CommonSearchBarComponent } from '../shared/common-search-bar/common-search-bar.component';
import { Subject, takeUntil } from 'rxjs';
import { SkeletonLoaderComponent } from '../shared/skeleton-loader/skeleton-loader.component';
import { RequestTracker } from '../../utilities/services/loading/request-tracker';
import { TemplateService } from '../../utilities/services/template/template.service';
@Component({
  selector: 'stupa-sub-licenses',
  standalone: true,
  imports: [
    CommonModule,
    PaginatorModule,
    GifLoaderComponent,
    SkeletonLoaderComponent,
    RouterOutlet,
    TranslateModule,
    NoDataFoundComponent,
    CommonSearchBarComponent,
  ],
  templateUrl: './sub-licenses.component.html',
  styleUrl: './sub-licenses.component.scss',
})
export class SubLicensesComponent implements OnInit, OnDestroy {
  readonly listLoading = new RequestTracker();
  role_list: any = [];
  selected_role: any = null;
  sublicenses: any = [];
  user_role_id: any = null;
  token: any = null;
  loggedInUser: any = null;
  subLicensesCount: any = 0;
  per_page: any = 12;
  first: any = 0;
  page_num: any = 1;
  isLoading: boolean = false;
  searchQuery: string = '';
  screenWidth: number = 0;
  private readonly destroy$ = new Subject<void>();
  searchBarExpanded: boolean = false;
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.screenWidth = globalThis.window.innerWidth;
  }
  constructor(
    private readonly translateService: TranslateService,
    private readonly languageService: LanguageService,
    private readonly roleService: RoleServiceService,
    private readonly encryptDecryptService: EncryptDecryptService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly subLicenseManagementService: SubLicenseManagementService,
    private readonly templateService: TemplateService,
  ) {
    this.screenWidth = globalThis.window.innerWidth;
    this.token = localStorage.getItem('token');
  }

  /** True when the OTT layout (template 1) is active — see {@link TemplateService}. */
  get isTemplate1(): boolean {
    return this.templateService.isTemplate1;
  }
  ngOnInit(): void {
    try {
      if (this.token && this.token.trim() !== '') {
        const tokenParts = this.token.split('.');
        if (tokenParts.length >= 2 && tokenParts[1]) {
          const responseData = JSON.parse(atob(tokenParts[1]));
          this.loggedInUser = responseData;
        } else {
          console.warn('Invalid token format');
          this.loggedInUser = null;
        }
      } else {
        this.loggedInUser = null;
      }
    } catch (error) {
      console.error('Error decoding token:', error);
      this.loggedInUser = null;
    }
    this.translateService.use(this.languageService.getCurrentLanguage());
    this.user_role_id = this.resolveEffectiveUserRoleId();
    this.get_roles();
  }

  /**
   * Switched user id from session when set — never falls back to local `user_role_id` in that case.
   * Order: `switch_user_role_id`, then `switch_user_id` (same pattern as registration).
   */
  private parseSessionSwitchUserRoleId(): number | null {
    if (!globalThis.window?.sessionStorage) {
      return null;
    }
    for (const key of ['switch_user_role_id', 'switch_user_id'] as const) {
      const raw = sessionStorage.getItem(key);
      if (!raw || raw === '-1') continue;
      const n = Number.parseInt(raw, 10);
      if (!Number.isNaN(n) && n >= 0) {
        return n;
      }
    }
    return null;
  }

  /** `user_role_id` for API calls: session switch id when present, else decrypted localStorage. */
  private resolveEffectiveUserRoleId(): number {
    const switched = this.parseSessionSwitchUserRoleId();
    if (switched !== null) {
      return switched;
    }
    return JSON.parse(
      this.encryptDecryptService.decryptUsingAES256(
        localStorage.getItem('user_role_id'),
      ) || '1',
    );
  }

  get_roles() {
    this.subLicenseManagementService.getRoles().pipe(this.listLoading.track()).subscribe({
      next: (res: any) => {
        this.role_list = res.data[0].roles;
        // Check if there's a stored selected role
        const storedRole = this.subLicenseManagementService.getSelectedRole();
        if (
          storedRole &&
          this.role_list.some((r: any) => r.role_id === storedRole.role_id)
        ) {
          // Use the stored role if it exists in the role list
          this.selected_role = this.role_list.find(
            (r: any) => r.role_id === storedRole.role_id,
          );
        } else {
          // Otherwise use the first role
          this.selected_role = this.role_list[0];
        }

        // Store the selected role in the service
        this.subLicenseManagementService.setSelectedRole(this.selected_role);
      },
      error: (err: any) => {

      },
      complete: () => {
        this.get_children_hierarchy();
      },
    });
  }
  select_role(role: any) {
    this.selected_role = role;
    // Store the selected role in the service
    this.subLicenseManagementService.setSelectedRole(this.selected_role);
    this.first = 0;
    this.page_num = 1;
    this.per_page = 12;
    this.get_children_hierarchy();
  }

  get_children_hierarchy() {
    this.isLoading = true;
    const effectiveUserRoleId = this.resolveEffectiveUserRoleId();
    this.user_role_id = effectiveUserRoleId;
    this.roleService
      .get_children_hierarchy(
        effectiveUserRoleId,
        this.selected_role.role_id,
        this.page_num,
        this.per_page,
        true,
        true,
        true,
        this.searchQuery,
      )
      .pipe(this.listLoading.track(), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.sublicenses = res.data;
          this.subLicensesCount = res.count;
          this.isLoading = false;
        },
        error: (err: any) => {

          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  setHeaderValue(header: any, value: any) {
    let valueIndex = this.sublicenses?.headers?.findIndex(
      (h: any) => h.name == header,
    );

    if (valueIndex === -1) {
      return '';
    }
    return value[valueIndex];
  }

  onPageChange(event: any) {
    this.page_num = event.page + 1;
    this.first = event.first;
    this.per_page = event.rows;
    this.get_children_hierarchy();
  }
  onPageSizeChange() {
    this.first = 0;
    this.page_num = 1;
    this.get_children_hierarchy();
  }

  navigateToSubLicenseManagement(sublicense: any) {
    let id = this.setHeaderValue('Id', sublicense);
    // Store the selected role in the service before navigating
    this.subLicenseManagementService.setSelectedRole(this.selected_role);
    this.router.navigate(['sub-license-management', id, 1, 1], {
      relativeTo: this.route,
    });
  }

  isBaseRoute(): boolean {
    const url = this.router.url;
    // Show list only if we're on the base route, not on a child route
    return (
      url === '/sub-licenses' ||
      (url.startsWith('/sub-licenses') &&
        !url.includes('/sub-license-management'))
    );
  }
  openLink(url: string) {
    globalThis.window.open(url, '_blank');
  }
  onSearchChange(searchQuery: string) {
    this.searchQuery = searchQuery;
    this.get_children_hierarchy();
  }
  onSearchSubmit(searchQuery: string) {
    this.searchQuery = searchQuery;
    this.get_children_hierarchy();
  }
  is_image(url: string) {
    if (url && (url.startsWith('http') || url.startsWith('https'))) {
      return true;
    }
    return false;
  }
  getAbbrivation(fullName: string | null | undefined): string {
    if (!fullName) {
      return '';
    }

    return fullName
      .trim()
      .split(/\s+/) // split by spaces
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
  }
  onLinkClick(event: Event) {
    event.stopPropagation(); // stops parent click
    // DO NOT preventDefault → let the link open normally
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchBarInputExpanded(expanded: boolean): void {
    this.searchBarExpanded = expanded;
  }

  trackById(index: number, item: unknown): string | number {
    if (item != null && typeof item === 'object' && 'id' in item) {
      const id = (item as { id?: string | number }).id;
      if (id != null) return id;
    }
    return index;
  }
}
